import { buildJobRecommendationQuery, type JobRecommendationProfile } from '#shared/utils/jobRecommendation'

/**
 * Composable for caching personalization query built from user profile.
 * This prevents re-embedding the same profile data on every search request.
 *
 * Cache is stored in localStorage with a 24-hour TTL and invalidated when profile changes.
 */

interface CachedQuery {
    query: string
    profileHash: string
    timestamp: number
}

interface PersonalizationSources {
    interests?: string[]
    skills?: string[]
    goals?: string[]
    causes?: string[]
    growthAreas?: string[]
    experience?: string[]
}

function toStringArray(values: unknown): string[] {
    if (!values) return []

    if (Array.isArray(values)) {
        return values.flatMap((value) => {
            if (typeof value === 'string') return value ? [value] : []
            if (typeof value === 'object' && value !== null) {
                const record = value as Record<string, unknown>
                const candidate = [record.label, record.id, record.info, record.text].find((item) => typeof item === 'string' && item)
                return typeof candidate === 'string' ? [candidate] : []
            }
            return []
        })
    }

    return typeof values === 'string' ? [values] : []
}

function getExperienceTexts(profile: NonNullable<ReturnType<typeof useApplicationStore>['userProfile']>): string[] {
    return profile.jobs?.map((job) => [job.position, job.org, job.desc].filter(Boolean).join(' ')).filter(Boolean) || []
}

function getGrowthGoalTexts(profile: NonNullable<ReturnType<typeof useApplicationStore>['userProfile']>): string[] {
    return profile.growthGoals?.map((goal) => goal.text).filter(Boolean) || []
}

const CACHE_KEY = 'growni_personalization_query'
const CACHE_TTL_MS = 24 * 60 * 60 * 1000 // 24 hours

// Typesense rejects GET searches whose query string exceeds 4000 characters, and the
// listing pages then silently fall back to newest-first. Job descriptions in the profile
// easily push the text past that, so keep it well below the limit once URL-encoded
// (Slovak diacritics take 6 characters each) to leave room for filter_by and the rest.
const MAX_QUERY_LENGTH = 1500
const MAX_ENCODED_QUERY_LENGTH = 2500

// In-memory cache for SSR/fast access
let memoryCache: CachedQuery | null = null

/**
 * Generate a simple hash from profile data to detect changes
 */
function hashProfile(sources: PersonalizationSources): string {
    const data = [
        ...(sources.interests || []),
        ...(sources.skills || []),
        ...(sources.goals || []),
        ...(sources.causes || []),
        ...(sources.growthAreas || []),
        ...(sources.experience || [])
    ].sort().join('|')

    // Simple hash function
    let hash = 0
    for (let i = 0; i < data.length; i++) {
        const char = data.charCodeAt(i)
        hash = ((hash << 5) - hash) + char
        hash = hash & hash // Convert to 32bit integer
    }
    return hash.toString(36)
}

/**
 * Build personalization query string from sources
 */
function buildQuery(sources: PersonalizationSources): string {
    const parts: string[] = []

    if (sources.interests?.length) {
        parts.push(sources.interests.join(' '))
    }
    if (sources.skills?.length) {
        parts.push(sources.skills.join(' '))
    }
    if (sources.goals?.length) {
        parts.push(sources.goals.join(' '))
    }
    if (sources.causes?.length) {
        parts.push(sources.causes.join(' '))
    }
    if (sources.growthAreas?.length) {
        parts.push(sources.growthAreas.join(' '))
    }
    if (sources.experience?.length) {
        parts.push(sources.experience.join(' '))
    }

    return parts.join(' ').trim()
}

/**
 * Cut the query on a word boundary so it stays under both length limits.
 * Interests and skills come first in the query, so they survive the cut.
 */
function capQueryLength(query: string): string {
    if (query.length <= MAX_QUERY_LENGTH && encodeURIComponent(query).length <= MAX_ENCODED_QUERY_LENGTH) {
        return query
    }

    const words: string[] = []
    let length = 0
    let encodedLength = 0
    for (const word of query.split(/\s+/)) {
        const separator = words.length ? 1 : 0
        const nextLength = length + separator + word.length
        const nextEncodedLength = encodedLength + separator * 3 + encodeURIComponent(word).length
        if (nextLength > MAX_QUERY_LENGTH || nextEncodedLength > MAX_ENCODED_QUERY_LENGTH) break
        words.push(word)
        length = nextLength
        encodedLength = nextEncodedLength
    }

    return words.join(' ')
}

/**
 * Load cache from localStorage
 */
function loadCache(): CachedQuery | null {
    if (!import.meta.client) return memoryCache

    try {
        const cached = localStorage.getItem(CACHE_KEY)
        if (cached) {
            const parsed = JSON.parse(cached) as CachedQuery
            // Check if cache is still valid
            if (Date.now() - parsed.timestamp < CACHE_TTL_MS) {
                memoryCache = parsed
                return parsed
            }
        }
    } catch (e) {
        console.warn('[PersonalizationQuery] Failed to load cache:', e)
    }
    return null
}

/**
 * Save cache to localStorage
 */
function saveCache(cache: CachedQuery): void {
    memoryCache = cache

    if (!import.meta.client) return

    try {
        localStorage.setItem(CACHE_KEY, JSON.stringify(cache))
    } catch (e) {
        console.warn('[PersonalizationQuery] Failed to save cache:', e)
    }
}

/**
 * Clear the personalization cache
 */
function clearCache(): void {
    memoryCache = null

    if (!import.meta.client) return

    try {
        localStorage.removeItem(CACHE_KEY)
    } catch (e) {
        // Ignore
    }
}

export function usePersonalizationQuery() {
    const applicationStore = useApplicationStore()

    /**
     * Get or build personalization query for mentors
     */
    const getMentorQuery = computed((): string => {
        const profile = applicationStore.userProfile
        if (!profile) return ''

        const sources: PersonalizationSources = {
            interests: [
                ...(profile.interests || []),
                ...(profile.mentorProfile?.interests || [])
            ],
            skills: toStringArray(profile.mentorProfile?.expertise),
            goals: getGrowthGoalTexts(profile)
        }

        return getOrBuildQuery(sources)
    })

    /**
     * Get or build personalization query for mentees (mentor searching for mentees)
     * Uses the mentor's expertise, interests, and experience as the query
     */
    const getMenteeQuery = computed((): string => {
        const profile = applicationStore.userProfile
        if (!profile) return ''

        const sources: PersonalizationSources = {
            interests: [
                ...(profile.mentorProfile?.interests || []),
                ...(profile.interests || [])
            ],
            skills: toStringArray(profile.mentorProfile?.expertise),
            experience: getExperienceTexts(profile)
        }

        return getOrBuildQuery(sources)
    })

    /**
     * Personalization query for jobs: short phrases about the kind of work, without employer
     * names, language codes or HTML (see shared/utils/jobRecommendation.ts)
     */
    const getJobQuery = computed((): string => {
        return buildJobRecommendationQuery(applicationStore.userProfile as JobRecommendationProfile | undefined)
    })

    /**
     * Get or build personalization query for volunteer projects
     */
    const getProjectQuery = computed((): string => {
        const profile = applicationStore.userProfile
        if (!profile) return ''

        const sources: PersonalizationSources = {
            interests: [
                ...(profile.interests || []),
                ...(profile.volunteerProfile?.interests || [])
            ],
            causes: getGrowthGoalTexts(profile)
        }

        return getOrBuildQuery(sources)
    })

    /**
     * Get or build personalization query for skills/education
     */
    const getSkillQuery = computed((): string => {
        const profile = applicationStore.userProfile
        if (!profile) return ''

        const sources: PersonalizationSources = {
            interests: [
                ...(profile.interests || []),
                ...(profile.mentorProfile?.interests || [])
            ],
            growthAreas: profile.universities?.map((u: any) => u.field).filter(Boolean) || []
        }

        return getOrBuildQuery(sources)
    })

    /**
     * Get cached query or build and cache a new one
     */
    function getOrBuildQuery(sources: PersonalizationSources): string {
        const profileHash = hashProfile(sources)
        const cached = loadCache()

        // Return cached query if profile hasn't changed. Queries cached before the
        // length cap existed can still be too long, so cap them on the way out too.
        if (cached && cached.profileHash === profileHash) {
            return capQueryLength(cached.query)
        }

        // Build new query and cache it
        const query = capQueryLength(buildQuery(sources))

        if (query) {
            saveCache({
                query,
                profileHash,
                timestamp: Date.now()
            })
        }

        return query
    }

    /**
     * Force refresh the cache (call after profile update)
     */
    function invalidateCache(): void {
        clearCache()
    }

    /**
     * Get raw profile data for API calls (homepage)
     * Returns cached structure for consistent embedding
     */
    const getProfileDataForApi = computed(() => {
        const profile = applicationStore.userProfile
        if (!profile) return null

        return {
            interests: [
                ...(profile.interests || []),
                ...(profile.mentorProfile?.interests || []),
                ...(profile.volunteerProfile?.interests || [])
            ],
            skills: [
                ...toStringArray(profile.mentorProfile?.expertise),
                ...toStringArray(profile.languages?.map((language) => language.code))
            ],
            goals: getGrowthGoalTexts(profile),
            causes: getGrowthGoalTexts(profile),
            experience: profile.jobs?.map((job) => job.position).filter(Boolean) || []
        }
    })

    return {
        // Domain-specific queries
        getMentorQuery,
        getMenteeQuery,
        getJobQuery,
        getProjectQuery,
        getSkillQuery,

        // Generic
        getOrBuildQuery,
        getProfileDataForApi,

        // Cache management
        invalidateCache,
        clearCache
    }
}
